import { ApplyProposalStageMeasurementsPayload, Proposal, ProposalStage } from "generated/graphql";
import { FeatureCollection } from "geojson";
import useCustomApplyProposalStageMeasurements from "hooks/proposals/useCustomApplyProposalStageMeasurements";
import useCustomSyncProposalStageMeasurements from "hooks/proposals/useCustomSyncProposalStageMeasurements";
import useMapMeasurements from "hooks/mapMeasurements/useMapMeasurements";
import { useCallback, useEffect, useState } from "react";
import {
  ExistingStageMeasurement,
  normalizeStageMapData,
  StageMeasurementDraft,
} from "modules/proposals/draft/measurements/stageMeasurements";

type UseStageMeasurementsArgs = {
  open: boolean;
  stage: ProposalStage;
  onSuccess: (proposal: Proposal) => void;
  onClose: () => void;
};

export default function useStageMeasurements({
  open,
  stage,
  onSuccess,
  onClose,
}: UseStageMeasurementsArgs) {
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [syncMeasurements] = useCustomSyncProposalStageMeasurements();
  const [applyMeasurements, applyContext] = useCustomApplyProposalStageMeasurements();

  const persistMeasurements = useCallback(
    async (
      nextMapData: FeatureCollection | null,
      nextMeasurements: StageMeasurementDraft[],
      options?: { refetch?: boolean }
    ) => {
      const normalizedMapData = normalizeStageMapData(nextMapData);
      if (!normalizedMapData) return null;

      return syncMeasurements({
        ...(options?.refetch === false ? { refetchQueries: [] } : {}),
        variables: {
          input: {
            proposalStageId: stage.id,
            mapData: JSON.stringify(normalizedMapData),
            measurements: nextMeasurements.map((m) => ({
              geojsonFeatureId: m.featureId,
              measurementType: m.measurementType,
              measurementJob: m.productId ? m.measurementJob : null,
              measurementValue: m.productId ? m.measurementValue : null,
              featureGeojson: m.featureGeojson,
              label: m.label,
              sortOrder: m.sortOrder,
              productId: m.productId,
              isScreenshotTarget: !!m.productId && !!m.isScreenshotTarget,
            })),
          },
        },
      });
    },
    [stage.id, syncMeasurements]
  );

  const measurements = useMapMeasurements({
    enabled: open,
    resetDeps: [stage.mapData, stage.stageMeasurements],
    mapDataJson: stage.mapData,
    existingMeasurements: (stage.stageMeasurements ?? []) as ExistingStageMeasurement[],
    persistMeasurements,
    canPersist: true,
    initialDrawerOpen: false,
  });

  useEffect(() => {
    if (open) setApplyError(null);
  }, [open]);

  const apply = useCallback(async () => {
    setApplyError(null);

    if (!measurements.assignedMeasurements.length) {
      setApplyError("Assign at least one measurement to a product before updating the proposal.");
      return;
    }

    const unassignedCount =
      measurements.measurements.length - measurements.assignedMeasurements.length;
    if (
      unassignedCount > 0 &&
      !window.confirm(
        `${unassignedCount} measurement${
          unassignedCount === 1 ? "" : "s"
        } are still unassigned. Continue updating the proposal?`
      )
    ) {
      return;
    }

    setApplying(true);
    try {
      await measurements.persist(measurements.mapData, measurements.measurements, {
        refetch: false,
      });

      const result = await applyMeasurements({
        variables: { input: { proposalStageId: stage.id } },
      });

      if (!applyContext.isSuccess(result.data)) {
        setApplyError("Unable to update the proposal from stage measurements.");
        return;
      }

      const payload = applyContext.getData(result) as ApplyProposalStageMeasurementsPayload | null;
      if (payload?.proposal) {
        onSuccess(payload.proposal);
      }
      onClose();
    } finally {
      setApplying(false);
    }
  }, [applyContext, applyMeasurements, measurements, onClose, onSuccess, stage.id]);

  return {
    mapData: measurements.mapData,
    measurements: measurements.measurements,
    selectedFeatureId: measurements.selectedFeatureId,
    isDrawerOpen: measurements.isDrawerOpen,
    persisting: measurements.persisting,
    isDirty: measurements.isDirty,
    applyError,
    products: measurements.products,
    productsLoading: measurements.productsLoading,
    applyLoading: applying,
    canSave: measurements.canSave,
    groupedAssignments: measurements.groupedAssignments,
    selectFeature: measurements.selectFeature,
    updateMeasurementProduct: measurements.updateMeasurementProduct,
    toggleScreenshot: measurements.toggleScreenshot,
    save: measurements.save,
    apply,
    handleMapChange: measurements.handleMapChange,
    handleFeatureSelect: measurements.handleFeatureSelect,
    toggleDrawer: measurements.toggleDrawer,
  };
}
